import { makeStyles } from '@material-ui/core/styles'
import { colors } from 'theme'

const hoverBackground = 'rgba(55, 53, 47, 0.08)'
const mutedText = 'rgba(55, 53, 47, 0.45)'
const popoverShadow = 'rgba(15, 15, 15, 0.05) 0 0 0 1px, rgba(15, 15, 15, 0.1) 0 3px 6px, rgba(15, 15, 15, 0.2) 0 9px 24px'
// Above MUI dialogs (1300) so the menus work inside AddAdviceModal
const menuZIndex = 1500
const aiTint = 'rgba(118, 0, 211, 0.07)'
const removedTint = 'rgba(235, 87, 87, 0.12)'

const smallButton = {
  height: 22,
  padding: '0 8px',
  border: 0,
  borderRadius: 4,
  fontSize: 12,
  fontFamily: 'inherit',
  cursor: 'pointer'
}


const useStyles = makeStyles((theme) => ({
  editor: {
    position: 'relative',
    borderRadius: 6,
    border: `1px solid ${colors.light.divider}`,
    backgroundColor: colors.light.paper,
    transition: 'border-color 120ms ease',
    '&:focus-within': {
      borderColor: theme.palette.primary.main
    },
    '&.error': {
      borderColor: `${colors.light.error}!important`
    },
    '& .ProseMirror': {
      minHeight: 300,
      padding: '16px 24px 40px 56px',
      outline: 'none',
      fontSize: 15,
      color: colors.light.textPrimary,
      [theme.breakpoints.down('sm')]: {
        padding: '12px 16px 16px'
      }
    },
    '& .ProseMirror p.is-empty::before, & .ProseMirror h1.is-empty::before, & .ProseMirror h2.is-empty::before, & .ProseMirror h3.is-empty::before': {
      content: 'attr(data-placeholder)',
      color: mutedText,
      float: 'left',
      height: 0,
      pointerEvents: 'none'
    },
    '& .ProseMirror-selectednode': {
      borderRadius: 3,
      backgroundColor: 'rgba(118, 0, 211, 0.08)',
      boxShadow: '0 0 0 2px rgba(118, 0, 211, 0.25)'
    },
    '& .ProseMirror .selectedCell': {
      backgroundColor: 'rgba(118, 0, 211, 0.08)'
    },
    '& .ProseMirror ul[data-type="taskList"] input[type="checkbox"]': {
      cursor: 'pointer'
    },
    // AI proposals under review (see aiReview.js)
    '& .ProseMirror .ai-removed': {
      backgroundColor: removedTint,
      textDecoration: 'line-through',
      textDecorationColor: 'rgba(235, 87, 87, 0.6)'
    },
    '& .ProseMirror .ai-removed-block': {
      boxShadow: `inset 3px 0 0 ${colors.light.error}`,
      opacity: 0.6
    },
    '& .ProseMirror .ai-removed-empty': {
      display: 'none'
    },
    '& .ProseMirror .ai-proposal': {
      backgroundColor: aiTint,
      borderRadius: 3,
      cursor: 'default',
      '&.block': {
        display: 'block',
        margin: '4px 0',
        padding: '2px 8px',
        boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`
      },
      '&.block > :first-child': {
        marginTop: 0
      }
    },
    '& .ProseMirror .ai-caret': {
      display: 'inline-block',
      width: 7,
      height: '1em',
      marginLeft: 2,
      verticalAlign: 'text-bottom',
      backgroundColor: theme.palette.primary.main,
      animation: '$aiBlink 1s steps(2, start) infinite'
    },
    '& .ProseMirror .ai-proposal-controls': {
      display: 'inline-flex',
      gap: 4,
      marginLeft: 8,
      verticalAlign: 'middle',
      '& button': {
        ...smallButton,
        backgroundColor: colors.light.paper,
        color: colors.light.textSecondary,
        boxShadow: `0 0 0 1px ${colors.light.divider}`
      },
      '& button.accept': {
        backgroundColor: theme.palette.primary.main,
        color: colors.light.paper,
        boxShadow: 'none'
      }
    },
    '& .ProseMirror .ai-proposal.block > .ai-proposal-controls': {
      display: 'flex',
      margin: '4px 0 6px'
    },
    // Cards are links in saved HTML, but inside the editor they are blocks
    '& .ProseMirror a.rich-bookmark, & .ProseMirror a.rich-file, & .ProseMirror a.mention': {
      cursor: 'default'
    }
  },
  '@keyframes aiBlink': {
    to: { visibility: 'hidden' }
  },
  footer: {
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    height: 0
  },
  aiButton: {
    ...smallButton,
    position: 'absolute',
    right: 8,
    bottom: 8,
    height: 24,
    backgroundColor: colors.light.paper,
    color: theme.palette.primary.main,
    fontSize: 13,
    // Tint over the opaque paper so text scrolling underneath stays hidden
    '&:hover, &[data-active="true"]': {
      backgroundImage: `linear-gradient(${aiTint}, ${aiTint})`
    }
  },
  aiSparkle: {
    color: theme.palette.primary.main,
    fontSize: 14
  },
  aiStatus: {
    fontSize: 13,
    color: mutedText
  },
  aiPrompt: {
    width: 440,
    maxWidth: 'calc(100vw - 16px)'
  },
  aiPromptRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 6px 6px 12px',
    minHeight: 32,
    '& > $aiStatus': {
      flex: 1
    }
  },
  aiPromptInput: {
    flex: 1,
    minWidth: 0,
    height: 28,
    border: 0,
    outline: 'none',
    fontSize: 14,
    fontFamily: 'inherit',
    backgroundColor: 'transparent'
  },
  aiPromptFooter: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
    padding: '6px 8px',
    borderTop: `1px solid ${colors.light.divider}`,
    '& > $mediaError': {
      flex: 1,
      margin: '0 4px'
    }
  },
  aiActions: {
    maxHeight: 280,
    overflowY: 'auto',
    padding: '4px 0 6px',
    borderTop: `1px solid ${colors.light.divider}`,
    '& $slashItem': {
      padding: '6px 10px'
    }
  },
  aiPanel: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    zIndex: menuZIndex,
    display: 'flex',
    flexDirection: 'column',
    width: 380,
    maxWidth: '100vw',
    backgroundColor: colors.light.paper,
    boxShadow: popoverShadow,
    fontSize: 14,
    color: colors.light.textPrimary
  },
  aiPanelHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 8px 10px 16px',
    borderBottom: `1px solid ${colors.light.divider}`
  },
  aiPanelTitle: {
    flex: 1,
    fontWeight: 500
  },
  aiPanelMessages: {
    flex: 1,
    overflowY: 'auto',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  },
  aiPanelEmpty: {
    color: colors.light.textSecondary,
    lineHeight: 1.5
  },
  aiSuggestions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 6,
    marginTop: 12
  },
  aiSuggestion: {
    padding: '6px 10px',
    border: `1px solid ${colors.light.divider}`,
    borderRadius: 14,
    background: 'transparent',
    color: colors.light.textPrimary,
    fontSize: 13,
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: hoverBackground
    }
  },
  aiMessage: {
    maxWidth: '100%',
    lineHeight: 1.5,
    '&.user': {
      alignSelf: 'flex-end',
      maxWidth: '85%',
      padding: '6px 12px',
      borderRadius: 12,
      backgroundColor: hoverBackground
    }
  },
  aiMessageQuote: {
    marginBottom: 4,
    paddingLeft: 8,
    borderLeft: `2px solid ${colors.light.divider}`,
    color: mutedText,
    fontSize: 12,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  aiMessageText: {
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere'
  },
  aiChangeSummary: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 6,
    padding: '4px 6px 4px 10px',
    borderRadius: 6,
    backgroundColor: aiTint,
    '& > :first-child': {
      flex: 1
    }
  },
  aiLink: {
    padding: 0,
    border: 0,
    background: 'transparent',
    color: theme.palette.primary.main,
    fontSize: 13,
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  aiPanelInput: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 6,
    padding: 12,
    borderTop: `1px solid ${colors.light.divider}`,
    '& textarea': {
      flex: 1,
      minWidth: 0,
      padding: '6px 8px',
      border: `1px solid ${colors.light.divider}`,
      borderRadius: 6,
      fontSize: 14,
      fontFamily: 'inherit',
      lineHeight: 1.4,
      resize: 'none',
      outline: 'none',
      '&:focus': {
        borderColor: theme.palette.primary.main
      }
    }
  },
  blockHandle: {
    position: 'absolute',
    left: 8,
    display: 'flex',
    alignItems: 'center',
    height: 24,
    zIndex: 1,
    '@media (hover: none)': {
      display: 'none'
    },
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  handleButton: {
    width: 20,
    height: 24,
    padding: 0,
    border: 0,
    borderRadius: 4,
    background: 'transparent',
    color: mutedText,
    fontSize: 18,
    lineHeight: '24px',
    textAlign: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
      backgroundColor: hoverBackground
    },
    '&.drag': {
      fontSize: 14,
      letterSpacing: -3,
      cursor: 'grab'
    }
  },
  popover: {
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: menuZIndex,
    backgroundColor: colors.light.paper,
    borderRadius: 6,
    boxShadow: popoverShadow,
    fontSize: 14,
    color: colors.light.textPrimary
  },
  slashMenu: {
    width: 300,
    maxHeight: 340,
    overflowY: 'auto',
    padding: '6px 0'
  },
  menuLabel: {
    padding: '6px 14px',
    fontSize: 11,
    fontWeight: 500,
    color: mutedText,
    textTransform: 'uppercase',
    letterSpacing: 0.4
  },
  menuEmpty: {
    padding: '6px 14px',
    color: mutedText
  },
  slashItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    margin: '0 4px',
    padding: '4px 10px',
    borderRadius: 4,
    cursor: 'pointer',
    '&[data-active="true"], &:hover': {
      backgroundColor: hoverBackground
    },
    '&[data-selected="true"]': {
      color: theme.palette.primary.main
    }
  },
  slashIcon: {
    flex: '0 0 auto',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${colors.light.divider}`,
    borderRadius: 4,
    backgroundColor: colors.light.paper,
    fontSize: 15,
    fontWeight: 600,
    color: colors.light.textSecondary,
    '&.small': {
      width: 24,
      height: 24,
      fontSize: 11
    },
    '&.round': {
      borderRadius: '50%',
      objectFit: 'cover'
    }
  },
  slashText: {
    minWidth: 0,
    '& > div': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }
  },
  menuHint: {
    padding: '6px 14px 2px',
    fontSize: 12,
    color: mutedText
  },
  uploadStatus: {
    position: 'absolute',
    right: 64,
    bottom: 8,
    padding: '2px 8px',
    borderRadius: 4,
    backgroundColor: colors.light.paper,
    backgroundImage: `linear-gradient(${hoverBackground}, ${hoverBackground})`,
    fontSize: 12,
    color: colors.light.textSecondary
  },
  mediaPicker: {
    width: 380,
    maxWidth: 'calc(100vw - 16px)'
  },
  mediaTabs: {
    display: 'flex',
    gap: 4,
    padding: '6px 8px 0',
    borderBottom: `1px solid ${colors.light.divider}`
  },
  mediaTab: {
    padding: '6px 8px',
    border: 0,
    borderBottom: '2px solid transparent',
    background: 'transparent',
    color: colors.light.textSecondary,
    fontSize: 14,
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&[data-active="true"]': {
      borderBottomColor: colors.light.textPrimary,
      color: colors.light.textPrimary
    }
  },
  mediaBody: {
    padding: 12,
    '& form': {
      display: 'flex',
      flexDirection: 'column',
      gap: 8
    },
    '& input': {
      width: '100%',
      boxSizing: 'border-box'
    }
  },
  mediaPrimary: {
    width: '100%',
    height: 32,
    border: 0,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main,
    color: colors.light.paper,
    fontSize: 14,
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:disabled': {
      opacity: 0.6,
      cursor: 'default'
    }
  },
  mediaError: {
    marginTop: 8,
    fontSize: 12,
    color: colors.light.error
  },
  slashTitle: {
    fontSize: 14
  },
  slashDescription: {
    fontSize: 12,
    color: mutedText
  },
  bubbleMenu: {
    display: 'flex',
    alignItems: 'center',
    padding: 3
  },
  tableMenu: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    maxWidth: 'calc(100vw - 16px)',
    padding: 3
  },
  turnIntoMenu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    width: 220,
    padding: '6px 0'
  },
  colorMenu: {
    position: 'absolute',
    top: 'calc(100% + 6px)',
    left: 0,
    padding: '6px 0'
  },
  colorRow: {
    display: 'flex',
    gap: 6,
    padding: '2px 14px 8px'
  },
  colorSwatch: {
    width: 26,
    height: 26,
    padding: 0,
    border: `1px solid ${colors.light.divider}`,
    borderRadius: 4,
    background: 'transparent',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'inherit',
    cursor: 'pointer',
    '&:hover': {
      borderColor: mutedText
    },
    '&[data-active="true"]': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
    }
  },
  colorSample: {
    padding: '0 3px',
    borderRadius: 2,
    fontWeight: 500
  },
  menuButton: {
    height: 28,
    minWidth: 28,
    padding: '0 8px',
    border: 0,
    borderRadius: 4,
    background: 'transparent',
    color: 'inherit',
    fontSize: 14,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: hoverBackground
    },
    '&[data-active="true"]': {
      color: theme.palette.primary.main
    },
    '&.danger:hover': {
      color: colors.light.error
    },
    '&.ai': {
      color: theme.palette.primary.main
    },
    '&.primary': {
      backgroundColor: theme.palette.primary.main,
      color: colors.light.paper,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark
      }
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default'
    },
    '&.icon': {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 4px'
    }
  },
  menuDivider: {
    alignSelf: 'stretch',
    width: 1,
    margin: '4px 2px',
    backgroundColor: colors.light.divider
  },
  linkEditor: {
    display: 'flex',
    alignItems: 'center',
    gap: 4
  },
  linkInput: {
    width: 260,
    height: 28,
    padding: '0 8px',
    border: `1px solid ${colors.light.divider}`,
    borderRadius: 4,
    fontSize: 14,
    outline: 'none',
    '&:focus': {
      borderColor: theme.palette.primary.main
    }
  }
}))

export default useStyles
